import {
    buildDashboardViewModel,
    EMPTY_DASHBOARD_DATA
} from './crmDashboardData';
import {
    buildGroupedOriginOptions,
    ORIGEM_GROUPS,
    ORIGEM_OPTIONS
} from './crmShared';
import { fetchFlask } from '../../utils/fetchFlask';
import {
    formatIsoDate,
    getDayCountInRange,
    normalizeDayStart
} from '../../utils/dateUtils';

export const CRM_MARKETING_ADS_API = 'crm/marketingAds';
export const NO_AD_LABEL = 'Sem anúncio';
export const NO_ORIGIN_ID = -1;
const UNNAMED_LABEL = 'Sem nome';

export const MARKETING_DASHBOARD_EMPTY_FILTERS = {
    origem_id: [],
    campanha: [],
    conjunto: [],
    anuncio: []
};

export const buildMarketingDashboardFilters = () => ({ ...MARKETING_DASHBOARD_EMPTY_FILTERS });

// Campanha, conjunto e anúncio filtram em cascata: cada um só oferece o que sobra dos anteriores.
export const MARKETING_DIMENSIONS = [
    { filterKey: 'campanha', field: 'campanha_nome', label: 'Campanha', allLabel: 'Todas as Campanhas' },
    { filterKey: 'conjunto', field: 'conjunto_nome', label: 'Conjunto', allLabel: 'Todos os Conjuntos' },
    { filterKey: 'anuncio', field: 'ad_nome', label: 'Anúncio', allLabel: 'Todos os Anúncios' }
];

const getOriginGroupIds = (groupValue) => ORIGEM_GROUPS.find((group) => group.value === groupValue).childIds;

export const MARKETING_CHANNELS = [
    { key: 'meta', label: 'Meta', color: 'var(--setta-amber)', originIds: getOriginGroupIds('canal_meta') },
    { key: 'google', label: 'Google', color: '#60a5fa', originIds: getOriginGroupIds('canal_google') }
];

const shiftOneMonthBack = (date) => {
    const lastDayOfPreviousMonth = new Date(date.getFullYear(), date.getMonth(), 0).getDate();
    return new Date(date.getFullYear(), date.getMonth() - 1, Math.min(date.getDate(), lastDayOfPreviousMonth));
};

// Padrão do painel do marketing: o mesmo trecho do mês anterior.
export const buildComparisonRange = ({ startDate, endDate }) => ({
    startDate: shiftOneMonthBack(startDate),
    endDate: shiftOneMonthBack(endDate)
});

const buildPeriodPayload = ({ startDate, endDate }) => JSON.stringify({
    data_inicio: formatIsoDate(startDate),
    data_fim: formatIsoDate(endDate)
});

export const fetchMarketingAds = async (dateRange) => {
    const response = await fetchFlask(CRM_MARKETING_ADS_API, buildPeriodPayload(dateRange));

    if (!Array.isArray(response?.data)) {
        throw new Error(response?.message || `Resposta inválida de ${CRM_MARKETING_ADS_API}.`);
    }

    return response.data;
};

export const fetchMarketingDashboardData = async ({ dateRange, comparisonRange }) => {
    const [rows, comparisonRows] = await Promise.all([
        fetchMarketingAds(dateRange),
        fetchMarketingAds(comparisonRange)
    ]);

    return { rows, comparisonRows };
};

export const getDimensionValue = (row, field) => {
    if (row.mkt_ad_id === null || row.mkt_ad_id === undefined) return NO_AD_LABEL;
    return row[field] || UNNAMED_LABEL;
};

const getOriginId = (row) => (row.origem_id === null || row.origem_id === undefined ? NO_ORIGIN_ID : Number(row.origem_id));

const matchesOrigin = (row, originIds) => (
    originIds.length === 0 || originIds.some((id) => Number(id) === getOriginId(row))
);

// Só entram as origens com algum dado no período; o nome vem do cadastro de origens do app.
export const buildOriginOptions = (rows) => {
    const presentIds = new Set(rows.map(getOriginId));
    const originOptions = [
        ...ORIGEM_OPTIONS.filter((option) => presentIds.has(option.id)),
        ...(presentIds.has(NO_ORIGIN_ID) ? [{ id: NO_ORIGIN_ID, label: 'Não informado' }] : [])
    ];

    return buildGroupedOriginOptions((a, b) => a.label.localeCompare(b.label, 'pt-BR'), originOptions);
};

const matchesDimension = (row, field, values) => values.length === 0 || values.includes(getDimensionValue(row, field));

export const applyMarketingFilters = (rows, filters) => rows.filter((row) => (
    matchesOrigin(row, filters.origem_id) &&
    MARKETING_DIMENSIONS.every(({ field, filterKey }) => matchesDimension(row, field, filters[filterKey]))
));

const clearDimensionsFrom = (filters, filterKey) => {
    const index = MARKETING_DIMENSIONS.findIndex((dimension) => dimension.filterKey === filterKey);

    return MARKETING_DIMENSIONS.slice(index).reduce(
        (nextFilters, dimension) => ({ ...nextFilters, [dimension.filterKey]: [] }),
        filters
    );
};

export const buildDimensionOptions = (rows, filters, filterKey) => {
    const { field } = MARKETING_DIMENSIONS.find((dimension) => dimension.filterKey === filterKey);
    const parentRows = applyMarketingFilters(rows, clearDimensionsFrom(filters, filterKey));
    const values = new Set(parentRows.map((row) => getDimensionValue(row, field)));

    return [...values]
        .sort((a, b) => a.localeCompare(b, 'pt-BR'))
        .map((value) => ({ value, label: value }));
};

// Ao trocar um filtro de cima ou o período, some o que ficou sem linha.
export const pruneMarketingFilters = (rows, filters) => {
    const presentOriginIds = new Set(rows.map(getOriginId));
    const originFilters = { ...filters, origem_id: filters.origem_id.filter((id) => presentOriginIds.has(Number(id))) };

    return MARKETING_DIMENSIONS.reduce((nextFilters, { filterKey }) => {
        const available = new Set(buildDimensionOptions(rows, nextFilters, filterKey).map((option) => option.value));
        return { ...nextFilters, [filterKey]: nextFilters[filterKey].filter((value) => available.has(value)) };
    }, originFilters);
};

const sumField = (rows, field) => rows.reduce((sum, row) => sum + Number(row[field] || 0), 0);

export const buildMarketingTotals = (rows) => ({
    impressions: sumField(rows, 'impressoes'),
    clicks: sumField(rows, 'cliques'),
    visits: sumField(rows, 'visitas'),
    cost: sumField(rows, 'custo'),
    leads: sumField(rows, 'leads'),
    sales: sumField(rows, 'vendas')
});

export const buildChannelTotals = (rows) => MARKETING_CHANNELS.map((channel) => ({
    ...channel,
    totals: buildMarketingTotals(rows.filter((row) => channel.originIds.includes(Number(row.origem_id))))
}));

const buildPhaseRows = (rows) => {
    const phaseById = new Map();

    rows.flatMap((row) => row.fases).forEach((phase) => {
        const current = phaseById.get(phase.fase_id) ?? { fase_id: phase.fase_id, quantidade_leads: 0, ticket: 0 };
        current.quantidade_leads += Number(phase.quantidade_leads);
        current.ticket += Number(phase.ticket || 0);
        phaseById.set(phase.fase_id, current);
    });

    return [...phaseById.values()];
};

export const buildMarketingFunnelData = (rows) => {
    const totals = buildMarketingTotals(rows);
    const crmViewModel = buildDashboardViewModel({ ...EMPTY_DASHBOARD_DATA, fase_id: buildPhaseRows(rows) });

    return [
        { label: 'Impressões', count: totals.impressions, totalTicket: null },
        { label: 'Cliques', count: totals.clicks, totalTicket: null },
        { label: 'Visitas', count: totals.visits, totalTicket: null },
        ...crmViewModel.funnelData
    ];
};

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export const buildDailySeries = (rows, { startDate, endDate }, field) => {
    const valueByDay = new Map();
    rows.forEach((row) => valueByDay.set(row.dia, (valueByDay.get(row.dia) ?? 0) + Number(row[field] || 0)));

    const firstDay = normalizeDayStart(startDate);
    return Array.from({ length: getDayCountInRange(startDate, endDate) }, (_, index) => {
        const day = addDays(firstDay, index);
        return { day, value: valueByDay.get(formatIsoDate(day)) ?? 0 };
    });
};

// Os dois períodos se alinham pelo dia de cada um ("dia 1 de cada período"), como no painel do marketing.
export const buildComparisonChartRows = ({ comparisonRange, comparisonRows, dateRange, field, rows }) => {
    const current = buildDailySeries(rows, dateRange, field);
    const comparison = buildDailySeries(comparisonRows, comparisonRange, field);

    return current.map((point, index) => ({
        key: formatIsoDate(point.day),
        day: point.day,
        comparisonDay: comparison[index]?.day ?? null,
        atual: point.value,
        comparacao: comparison[index]?.value ?? 0
    }));
};

export const buildDelta = (current, previous, lowerIsBetter = false) => {
    if (current === null || previous === null || !previous) return { tone: 'flat', text: '—' };

    const change = ((current - previous) / previous) * 100;
    if (Math.abs(change) < 0.5) return { tone: 'flat', text: 'estável' };

    const isGood = lowerIsBetter ? change < 0 : change > 0;
    const text = `${change > 0 ? '▲ +' : '▼ '}${change.toFixed(1).replace('.', ',')}%`;

    return { tone: isGood ? 'up' : 'down', text };
};

export const costPer = (cost, count) => (count > 0 ? cost / count : null);
